import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Phone, AlertTriangle, TrendingUp, Clock, ChevronDown, ChevronUp,
  Users, ArrowRight, Smile, Zap, Award, UserCheck
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell
} from 'recharts';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Calls.css';

const DIRECTIONS = ['inbound', 'outbound', 'internal'];
const SENTIMENTS = ['positive', 'negative', 'neutral'];
const EMOTIONS = ['joy', 'anger', 'frustration', 'satisfaction', 'disappointment', 'neutral', 'excitement', 'concern'];

// Distinct hues for the team-emotion pie so repeated colors don't cluster
const EMOTION_COLORS = ['#60a5fa', '#34d399', '#f59e0b', '#c4b5fd', '#f472b6', '#22d3ee', '#fb7185', '#a3e635'];

const formatDuration = (ms) => {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const formatTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const PAGE_SIZES = [10, 25, 50, 100];

const Calls = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [emotion, setEmotion] = useState('');
  const [urgency, setUrgency] = useState('');
  const [direction, setDirection] = useState('');
  const [agent, setAgent] = useState('');
  const [issueCategory, setIssueCategory] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Options for the agent + category dropdowns, fetched once on mount.
  const [options, setOptions] = useState({ agents: [], categories: [], emotions: [] });

  // Tabs
  const [activeTab, setActiveTab] = useState('calls'); // 'calls' | 'agents'
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');

  const { showError } = useToast();

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const params = { page, limit: pageSize, status: 'analyzed' };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (sentiment) params.sentiment = sentiment;
      if (emotion) params.emotion = emotion;
      if (urgency) params.urgency = urgency;
      if (direction) params.direction = direction;
      if (agent) params.agent = agent;
      if (issueCategory) params.issueCategory = issueCategory;
      if (customerPhone) params.customerPhone = customerPhone;
      if (search) params.search = search;

      const res = await api.get('/api/calls', { params });
      if (res.data.success) {
        setCalls(res.data.calls || []);
        setKpis(res.data.kpis || null);
        setPagination(res.data.pagination || { page: 1, totalPages: 1, total: 0 });
      }
    } catch (err) {
      console.error(err);
      showError('Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch filter options once on mount
  useEffect(() => {
    api.get('/api/calls/filter-options')
      .then(res => { if (res.data.success) setOptions(res.data); })
      .catch(err => console.warn('Failed to load filter options:', err.message));
  }, []);

  // Load agents the first time the Agents tab is opened
  useEffect(() => {
    if (activeTab !== 'agents' || agents.length > 0) return;
    (async () => {
      try {
        setAgentsLoading(true);
        const res = await api.get('/api/calls/agents');
        if (res.data.success) setAgents(res.data.agents || []);
      } catch (err) {
        console.error(err);
        showError('Failed to load agents');
      } finally {
        setAgentsLoading(false);
      }
    })();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCalls();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSentiment('');
    setEmotion('');
    setUrgency('');
    setDirection('');
    setAgent('');
    setIssueCategory('');
    setCustomerPhone('');
    setSearch('');
    setPage(1);
    setTimeout(fetchCalls, 0);
  };

  const sentimentClass = (s) => {
    if (s === 'positive') return 'positive';
    if (s === 'negative') return 'negative';
    return 'neutral';
  };

  const formatShort = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const visibleAgents = (agents || []).filter(a => {
    if (!agentSearch) return true;
    const q = agentSearch.toLowerCase();
    return (a.agent_name || '').toLowerCase().includes(q)
        || (a.agent_extension || '').toLowerCase().includes(q);
  });

  // Team aggregates — computed once per agents change
  const teamStats = useMemo(() => {
    if (!agents || agents.length === 0) return null;

    const sentiment = { positive: 0, neutral: 0, negative: 0 };
    const emotions = {};
    let totalCalls = 0, totalAnalyzed = 0, totalUrgent = 0;

    for (const a of agents) {
      totalCalls += a.total_calls || 0;
      totalAnalyzed += a.analyzed_calls || 0;
      totalUrgent += a.urgent_count || 0;
      const s = a.sentiment_counts || {};
      sentiment.positive += s.positive || 0;
      sentiment.neutral  += s.neutral  || 0;
      sentiment.negative += s.negative || 0;
      const ec = a.emotion_counts || {};
      for (const [k, v] of Object.entries(ec)) emotions[k] = (emotions[k] || 0) + v;
    }

    // "Champion" agents
    const withAnalyzed = agents.filter(a => (a.analyzed_calls || 0) > 0);
    const mostActive = [...agents].sort((a, b) => b.total_calls - a.total_calls)[0];
    const mostUrgent = [...agents].sort((a, b) => (b.urgent_count || 0) - (a.urgent_count || 0))[0];
    const bestSentiment = [...withAnalyzed]
      .filter(a => a.avg_sentiment_score != null)
      .sort((a, b) => b.avg_sentiment_score - a.avg_sentiment_score)[0];
    const worstSentiment = [...withAnalyzed]
      .filter(a => a.avg_sentiment_score != null)
      .sort((a, b) => a.avg_sentiment_score - b.avg_sentiment_score)[0];

    const topEmotions = Object.entries(emotions)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));

    const barsCalls = [...agents]
      .sort((a, b) => b.total_calls - a.total_calls)
      .slice(0, 12)
      .map(a => ({
        name: (a.agent_name || a.agent_extension).length > 14
          ? (a.agent_name || a.agent_extension).slice(0, 13) + '…'
          : (a.agent_name || a.agent_extension),
        ext: a.agent_extension,
        calls: a.total_calls,
        urgent: a.urgent_count || 0
      }));

    return {
      totalAgents: agents.length,
      totalCalls,
      totalAnalyzed,
      totalUrgent,
      sentiment,
      topEmotions,
      mostActive,
      mostUrgent,
      bestSentiment,
      worstSentiment,
      barsCalls
    };
  }, [agents]);

  return (
    <div className="calls-page">
      <div className="calls-header">
        <div>
          <h1 className="calls-title">Avaya Calls</h1>
          <p className="calls-subtitle">Call recordings, transcripts, and AI analysis</p>
        </div>
      </div>

      {/* KPIs — always shown, above tabs */}
      {kpis && (
        <div className="calls-kpis">
          <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="kpi-label"><Phone size={14} /> Total calls</div>
            <div className="kpi-value">{kpis.total}</div>
          </motion.div>
          <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <div className="kpi-label"><AlertTriangle size={14} /> Urgent</div>
            <div className="kpi-value negative">{kpis.urgentCount}</div>
          </motion.div>
          <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="kpi-label"><TrendingUp size={14} /> Positive</div>
            <div className="kpi-value positive">{kpis.sentimentBreakdown?.positive ?? 0}</div>
          </motion.div>
          <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <div className="kpi-label">Negative</div>
            <div className="kpi-value negative">{kpis.sentimentBreakdown?.negative ?? 0}</div>
          </motion.div>
        </div>
      )}

      {/* Tabs — under KPIs */}
      <div className="calls-tabs">
        <button
          className={`calls-tab ${activeTab === 'calls' ? 'active' : ''}`}
          onClick={() => setActiveTab('calls')}
        >
          <Phone size={16} /> Calls
        </button>
        <button
          className={`calls-tab ${activeTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveTab('agents')}
        >
          <Users size={16} /> Agents
        </button>
      </div>

      {activeTab === 'agents' && (
        <>
          {agentsLoading ? (
            <div className="calls-loading">
              <div className="spinner" />
              <p>Loading agents...</p>
            </div>
          ) : !teamStats ? (
            <div className="calls-empty">
              <Users size={48} />
              <p>No agents found.</p>
            </div>
          ) : (
            <>
              {/* Team-overview KPI row */}
              <div className="calls-kpis">
                <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <div className="kpi-label"><Users size={14} /> Agents</div>
                  <div className="kpi-value">{teamStats.totalAgents}</div>
                  <div className="kpi-sub">{teamStats.totalCalls.toLocaleString()} calls · {teamStats.totalAnalyzed.toLocaleString()} analyzed</div>
                </motion.div>
                <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                  <div className="kpi-label"><Zap size={14} color="#60a5fa" /> Most active</div>
                  <div className="kpi-value" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>
                    {teamStats.mostActive?.agent_name || teamStats.mostActive?.agent_extension}
                  </div>
                  <div className="kpi-sub">{teamStats.mostActive?.total_calls} calls</div>
                </motion.div>
                <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <div className="kpi-label"><AlertTriangle size={14} color="#ef4444" /> Most urgent</div>
                  <div className="kpi-value" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>
                    {teamStats.mostUrgent?.agent_name || teamStats.mostUrgent?.agent_extension}
                  </div>
                  <div className="kpi-sub">{teamStats.mostUrgent?.urgent_count || 0} urgent</div>
                </motion.div>
                {teamStats.bestSentiment && (
                  <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <div className="kpi-label"><Award size={14} color="#10b981" /> Best sentiment</div>
                    <div className="kpi-value positive" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>
                      {teamStats.bestSentiment.agent_name || teamStats.bestSentiment.agent_extension}
                    </div>
                    <div className="kpi-sub">{teamStats.bestSentiment.avg_sentiment_score?.toFixed(2)}</div>
                  </motion.div>
                )}
                {teamStats.worstSentiment && teamStats.worstSentiment !== teamStats.bestSentiment && (
                  <motion.div className="kpi-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <div className="kpi-label"><UserCheck size={14} color="#f87171" /> Needs coaching</div>
                    <div className="kpi-value negative" style={{ fontSize: '1.3rem', lineHeight: 1.1 }}>
                      {teamStats.worstSentiment.agent_name || teamStats.worstSentiment.agent_extension}
                    </div>
                    <div className="kpi-sub">{teamStats.worstSentiment.avg_sentiment_score?.toFixed(2)}</div>
                  </motion.div>
                )}
              </div>

              {/* Charts: workload bar + sentiment/emotion pies */}
              <div className="agents-charts">
                {/* Workload (calls + urgent stacked) */}
                <div className="calls-panel">
                  <div className="calls-panel-head">
                    <div className="calls-panel-title">
                      <Phone size={16} color="#60a5fa" /> Workload (top {teamStats.barsCalls.length})
                    </div>
                    <div className="calls-panel-count">calls + urgent</div>
                  </div>
                  <div className="calls-chart-box">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={teamStats.barsCalls} layout="vertical" margin={{ top: 6, right: 14, left: 12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                        <XAxis type="number" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.55)" fontSize={11} tickLine={false} width={110} />
                        <Tooltip
                          contentStyle={{ background: 'rgba(20,20,25,0.95)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: '0.8rem' }}
                          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        />
                        <Bar dataKey="calls"  stackId="a" fill="#60a5fa" name="Calls"  radius={[0, 0, 0, 0]} />
                        <Bar dataKey="urgent" stackId="a" fill="#ef4444" name="Urgent" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Team sentiment pie */}
                <div className="calls-panel">
                  <div className="calls-panel-head">
                    <div className="calls-panel-title">
                      <Smile size={16} color="#10b981" /> Team sentiment mix
                    </div>
                    <div className="calls-panel-count">
                      {(teamStats.sentiment.positive + teamStats.sentiment.neutral + teamStats.sentiment.negative).toLocaleString()} analyzed
                    </div>
                  </div>
                  <div className="calls-chart-box">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Positive', value: teamStats.sentiment.positive, color: '#10b981' },
                            { name: 'Neutral',  value: teamStats.sentiment.neutral,  color: '#f59e0b' },
                            { name: 'Negative', value: teamStats.sentiment.negative, color: '#ef4444' }
                          ]}
                          cx="50%" cy="50%"
                          innerRadius={60} outerRadius={95}
                          paddingAngle={2}
                          dataKey="value"
                          stroke="rgba(20,20,25,0.9)"
                          strokeWidth={2}
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#f59e0b" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: 'rgba(20,20,25,0.95)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: '0.8rem' }}
                          formatter={(v, n) => [Number(v).toLocaleString(), n]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="calls-pie-legend">
                    <span><span className="sw" style={{ background: '#10b981' }} /> Positive · {teamStats.sentiment.positive}</span>
                    <span><span className="sw" style={{ background: '#f59e0b' }} /> Neutral · {teamStats.sentiment.neutral}</span>
                    <span><span className="sw" style={{ background: '#ef4444' }} /> Negative · {teamStats.sentiment.negative}</span>
                  </div>
                </div>
              </div>

              {/* Emotion pie (wider, full width) */}
              {teamStats.topEmotions.length > 0 && (
                <div className="agents-charts agents-charts-alt">
                  <div className="calls-panel">
                    <div className="calls-panel-head">
                      <div className="calls-panel-title">
                        <Smile size={16} color="#c4b5fd" /> Team emotion mix
                      </div>
                      <div className="calls-panel-count">{teamStats.topEmotions.length} emotions</div>
                    </div>
                    <div className="calls-chart-box">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={teamStats.topEmotions}
                            cx="50%" cy="50%"
                            outerRadius={95}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="rgba(20,20,25,0.9)"
                            strokeWidth={2}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {teamStats.topEmotions.map((_, i) => (
                              <Cell key={i} fill={EMOTION_COLORS[i % EMOTION_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ background: 'rgba(20,20,25,0.95)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: '0.8rem' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Urgency bar (per-agent) */}
                  <div className="calls-panel">
                    <div className="calls-panel-head">
                      <div className="calls-panel-title">
                        <AlertTriangle size={16} color="#ef4444" /> Urgent calls per agent
                      </div>
                      <div className="calls-panel-count">{teamStats.totalUrgent} total</div>
                    </div>
                    <div className="calls-chart-box">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={teamStats.barsCalls} margin={{ top: 6, right: 14, left: 0, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} angle={-20} textAnchor="end" />
                          <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ background: 'rgba(20,20,25,0.95)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: '0.8rem' }}
                            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                          />
                          <Bar dataKey="urgent" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Agents table */}
              <div className="calls-filters" style={{ padding: '0.9rem 1.1rem' }}>
                <div className="search-wrap" style={{ width: '100%' }}>
                  <Search size={14} />
                  <input
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    placeholder="Search agents by name or extension…"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              {visibleAgents.length === 0 ? (
                <div className="calls-empty"><Users size={48} /><p>No agents match your search.</p></div>
              ) : (
                <div className="calls-table-container">
                  <table className="calls-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Ext</th>
                        <th>Calls</th>
                        <th>Urgent</th>
                        <th>Avg sentiment</th>
                        <th>Sentiment mix</th>
                        <th>Top emotion</th>
                        <th>Last call</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleAgents.map((a, i) => {
                        const s = a.sentiment_counts || { positive: 0, negative: 0, neutral: 0 };
                        const total = s.positive + s.negative + s.neutral || 1;
                        return (
                          <motion.tr
                            key={a.agent_extension}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: i * 0.02 }}
                            className="call-row"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/calls/agents/${encodeURIComponent(a.agent_extension)}`)}
                          >
                            <td>{a.agent_name || <span style={{ color: 'rgba(255,255,255,0.4)' }}>(no name)</span>}</td>
                            <td style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: '0.85rem' }}>{a.agent_extension}</td>
                            <td>
                              <div>{a.total_calls}</div>
                              {a.analyzed_calls !== a.total_calls && (
                                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>
                                  {a.analyzed_calls} analyzed
                                </div>
                              )}
                            </td>
                            <td>
                              {a.urgent_count > 0 ? (
                                <span className="badge negative"><AlertTriangle size={12} /> {a.urgent_count}</span>
                              ) : '0'}
                            </td>
                            <td>
                              {a.avg_sentiment_score != null
                                ? <span className={`badge ${a.avg_sentiment_score >= 0.6 ? 'positive' : a.avg_sentiment_score >= 0.4 ? 'neutral' : 'negative'}`}>
                                    {a.avg_sentiment_score.toFixed(2)}
                                  </span>
                                : '—'}
                            </td>
                            <td style={{ minWidth: 140 }}>
                              <div className="agent-mix-bar">
                                <div className="agent-mix-seg positive" style={{ width: `${(s.positive/total)*100}%` }} title={`${s.positive} positive`} />
                                <div className="agent-mix-seg neutral"  style={{ width: `${(s.neutral /total)*100}%` }} title={`${s.neutral} neutral`} />
                                <div className="agent-mix-seg negative" style={{ width: `${(s.negative/total)*100}%` }} title={`${s.negative} negative`} />
                              </div>
                            </td>
                            <td style={{ textTransform: 'capitalize' }}>{a.top_emotion || '—'}</td>
                            <td style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>{formatShort(a.last_call_at)}</td>
                            <td><ArrowRight size={14} color="rgba(255,255,255,0.4)" /></td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'calls' && (
        <>
      {/* Filters */}
      <div className="calls-filters">
        <button className="filters-toggle" onClick={() => setFiltersOpen(!filtersOpen)}>
          Filters {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {filtersOpen && (
          <form onSubmit={applyFilters} className="filters-grid">
            <label>Search (transcript, agent, caller)
              <div className="search-wrap">
                <Search size={14} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. refund" />
              </div>
            </label>
            <label>Customer phone
              <div className="search-wrap">
                <Search size={14} />
                <input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="e.g. 56029"
                  inputMode="tel"
                />
              </div>
            </label>
            <label>Agent
              <select value={agent} onChange={(e) => setAgent(e.target.value)}>
                <option value="">Any</option>
                {options.agents.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label>Category
              <select value={issueCategory} onChange={(e) => setIssueCategory(e.target.value)}>
                <option value="">Any</option>
                {options.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Start date
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label>End date
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
            <label>Sentiment
              <select value={sentiment} onChange={(e) => setSentiment(e.target.value)}>
                <option value="">Any</option>
                {SENTIMENTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Emotion
              <select value={emotion} onChange={(e) => setEmotion(e.target.value)}>
                <option value="">Any</option>
                {(options.emotions.length ? options.emotions : EMOTIONS).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Urgency
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)}>
                <option value="">Any</option>
                <option value="true">Urgent only</option>
                <option value="false">Non-urgent</option>
              </select>
            </label>
            <label>Direction
              <select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="">Any</option>
                {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <div className="filter-buttons">
              <button type="button" className="btn-secondary" onClick={clearFilters}>Clear</button>
              <button type="submit" className="btn-primary">Apply</button>
            </div>
          </form>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="calls-loading">
          <div className="spinner" />
          <p>Loading calls...</p>
        </div>
      ) : calls.length === 0 ? (
        <div className="calls-empty">
          <Phone size={48} />
          <p>No analyzed calls found for this filter.</p>
        </div>
      ) : (
        <div className="calls-table-container">
          <table className="calls-table">
            <thead>
              <tr>
                <th><Clock size={14} /> Start</th>
                <th>Duration</th>
                <th>Agent</th>
                <th>Customer</th>
                <th>Dir</th>
                <th>Sentiment</th>
                <th>Emotion</th>
                <th>Category</th>
                <th>Urgent</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c, i) => (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  className="call-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/calls/${c.id}`)}
                >
                  <td>
                    <Link
                      to={`/calls/${c.id}`}
                      className="call-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {formatTime(c.start_time)}
                    </Link>
                  </td>
                  <td>{formatDuration(c.duration_ms)}</td>
                  <td>
                    {c.agent_name
                      ? <>
                          <div>{c.agent_name}</div>
                          {c.agent_extension && (
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                              ext {c.agent_extension}
                            </div>
                          )}
                        </>
                      : '—'}
                  </td>
                  <td style={{ fontFamily: 'SF Mono, Menlo, monospace', fontSize: '0.85rem' }}>
                    {c.caller_number || '—'}
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{c.direction || '—'}</td>
                  <td>
                    {c.analysis?.sentiment && (
                      <span className={`badge ${sentimentClass(c.analysis.sentiment)}`}>
                        {c.analysis.sentiment}
                      </span>
                    )}
                  </td>
                  <td>{c.analysis?.detected_emotion || '—'}</td>
                  <td>{c.analysis?.issue_category || '—'}</td>
                  <td>{c.analysis?.urgency_flag ? <AlertTriangle size={16} color="#ef4444" /> : ''}</td>
                  <td className="summary-cell">
                    {c.analysis?.summary_bullets?.[0] || '—'}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {calls.length > 0 && (
        <div className="calls-pagination">
          <div className="pagination-left">
            <label className="page-size-label">
              Show
              <select
                value={pageSize}
                onChange={(e) => { setPage(1); setPageSize(parseInt(e.target.value)); }}
              >
                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              per page
            </label>
          </div>
          <div className="pagination-center">
            <button disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span className="page-info">
              Page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages || 1}</strong>
            </span>
            <button
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next ›
            </button>
            <button
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(pagination.totalPages)}
            >
              Last »
            </button>
          </div>
          <div className="pagination-right">
            <span className="total-info">{pagination.total.toLocaleString()} calls total</span>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Calls;
